import { useEffect, useState, useRef, useCallback } from "react";

const shouldActOnSwipe = (duration, delta) => {
  return (
    (duration > 50 && duration < 250 && delta.x !== 0 && delta.y !== 0) ||
    (duration >= 250 && Math.abs(delta.x) + Math.abs(delta.y) > 100)
  );
};

let htmlElement;
if (typeof document !== "undefined") {
  htmlElement = document.querySelector("html");
}

const useSwipe = ({ contentRef, key, block, direction }) => {
  const start = useRef();
  const [delta, setDelta] = useState({
    shouldActOnSwipe: false,
    x: 0,
    y: 0,
  });

  let movement =
    (direction === "vertical" ? Math.abs(delta.y) : Math.abs(delta.x)) > 20;

  useEffect(() => {
    if (htmlElement) {
      if (movement) {
        htmlElement.style.overflow = "hidden";
      }
      if (delta.shouldActOnSwipe) {
        htmlElement.style.overflow = "auto";
      }
    }
  }, [movement, delta.shouldActOnSwipe]);

  const onTouchMove = useCallback((event) => {
    // Do not swipe if the user is scrolling/pinching/etc.
    if (event.touches.length > 1 || (event.scale && event.scale !== 1)) {
      return;
    }

    const touch = event.touches[0];

    requestAnimationFrame(() => {
      setDelta((state) => ({
        shouldActOnSwipe: false,
        x: start.current.x - touch.pageX,
        y: start.current.y - touch.pageY,
      }));
    });
  }, []);

  const onTouchEnd = useCallback(
    (event) => {
      contentRef.current.removeEventListener("touchmove", onTouchMove);
      contentRef.current.removeEventListener("touchend", onTouchEnd);
      requestAnimationFrame(() => {
        const duration = start.current ? new Date() - start.current.time : 0;
        start.current = undefined;
        setDelta((state) => {
          const shouldAct = shouldActOnSwipe(duration, state);
          return {
            x: shouldAct ? state.x : 0,
            y: shouldAct ? state.y : 0,
            shouldActOnSwipe: shouldAct,
          };
        });
      });
    },
    [contentRef, onTouchMove]
  );

  const onTouchStart = useCallback(
    (event) => {
      if (start.current) {
        return;
      }
      const touch = event.touches[0];
      start.current = {
        x: touch.pageX,
        y: touch.pageY,
        time: +new Date(),
      };
      contentRef.current.addEventListener("touchmove", onTouchMove, {
        passive: true,
      });
      contentRef.current.addEventListener("touchend", onTouchEnd);
    },
    [contentRef, onTouchEnd, onTouchMove]
  );

  useEffect(() => {
    const element = contentRef.current;
    if (!element || block) {
      return;
    }

    element.addEventListener("touchstart", onTouchStart, { passive: true });

    return () => {
      element.removeEventListener("touchstart", onTouchStart);
      element.removeEventListener("touchmove", onTouchMove);
      element.removeEventListener("touchend", onTouchEnd);
    };
  }, [block, key, contentRef, onTouchEnd, onTouchMove, onTouchStart]);

  const resetDelta = () => {
    setDelta({
      shouldActOnSwipe: false,
      x: 0,
      y: 0,
    });
  };

  return [delta, resetDelta];
};

export default useSwipe;
